import type { AppMenuItemState, FooterItemProps } from "./AppMenuItem.types";
import type { GriffelStyle } from "@fluentui/react-components";
import {
  makeStyles,
  mergeClasses,
  useMenuItemStyles_unstable,
  shorthands,
} from "@fluentui/react-components";
import { customTokens } from "@1js/acui-theme";
// eslint-disable-next-line no-restricted-imports
import {
  HighContrastSelector,
  commonHighContrastHoverFocus,
  commonHighContrastColorDisabled,
  commonHighContrastHoveredDisabledStyles,
  commonHighContrastColorButtonRest,
  commonHighContrastColorHoverFocus,
  commonMediumContrastColorHoverFocus,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastBackgroundHoverFocus,
  commonMediumContrastBackgroundHoverFocus,
  useSelectedIconHighContrastStyles,
} from "@1js/acui-common/lib/styling/next";
import { useV9MenuEnabled } from "../../hooks/useNextComponentsEnabled";
import { getHighContrastNoAdjustStyle } from "@fluentui/style-utilities";

export const useAppMenuItemCustomStyles = makeStyles({
  root: {
    ...shorthands.borderRadius("4px"),
    ...shorthands.gap("8px", "0px"),
    ...shorthands.margin("0px", "6px"),
    ...shorthands.padding("0px", "6px"),
    ...shorthands.borderWidth("0px"),
    height: "32px",
    flexShrink: 0,
    alignItems: "center",
    minWidth: "224px",
    [`${HighContrastSelector}`]: {
      ...commonHighContrastColorButtonRest,
      "& [class*='secondaryContent']": {
        ...commonHighContrastColorButtonRest,
      },
      ":hover": {
        ...commonHighContrastHoverFocus,
        "& [class*='secondaryContent']": {
          ...commonHighContrastColorHoverFocus,
        },
        ...commonMediumContrastBackgroundHoverFocus,
        ...commonMediumContrastColorHoverFocus,
        ...(getHighContrastNoAdjustStyle() as GriffelStyle),
        "& i": {
          ...commonHighContrastBackgroundHoverFocus,
        },
      },
      ":hover:active": {
        ...commonHighContrastHoverFocus,
        "& [class*='secondaryContent']": {
          ...commonHighContrastColorHoverFocus,
        },
      },
    },
  },
  menuItems: {
    minWidth: "unset",
  },
  icon: {
    ...shorthands.padding("6px", "0px"),
    alignItems: "flex-start",
  },
  footerIcon: {
    width: "16px",
    height: "16px",
    display: "flex",
    padding: "0",
  },
  link: {
    float: "left",
    border: "none",
    background: "transparent",
    color: customTokens.colorBrandForeground1,
    cursor: "pointer",
    fontSize: customTokens.fontSizeBase200,
    marginBottom: customTokens.spacingVerticalXXS,
    marginLeft: customTokens.spacingHorizontalM,
    display: "flex",
    alignItems: "center",
    gap: customTokens.spacingVerticalSNudge,
    ":hover": {
      textDecoration: "underline",
      cursor: "pointer",
      backgroundColor: "transparent !important",
    },
    ":focus": {},
    "& i": {
      display: "none !important",
    },

    [`${HighContrastSelector}`]: {
      ...commonHighContrastColorButtonRest,

      ":hover": {
        ...commonHighContrastColorHoverFocus,
        ...commonHighContrastOutlineHoverFocus,
      },
      ":hover:active": {
        ...commonHighContrastHoverFocus,
      },
    },
  },
  footerButton: {
    position: "relative",
    padding: `${customTokens.spacingVerticalNone} ${customTokens.spacingHorizontalM}`,
    height: "unset",
    width: "unset",
    borderRadius: customTokens.borderRadiusMedium,
    fontSize: customTokens.fontSizeBase200,
    border: `1px solid ${customTokens.colorNeutralStroke1}`,
  },
  content: {
    alignItems: "center",
    fontFamily: customTokens.fontFamilyBase,
    ...shorthands.flex(1, 0),
    ...shorthands.padding("6px", "0px"),
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...shorthands.overflow("hidden"),
  },
  secondaryContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    fontFamily: customTokens.fontFamilyBase,
    ...shorthands.gap("2px"),
    ...shorthands.padding("6px", "0px", "6px", "4px"),
  },
  submenuIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    ...shorthands.padding("6px", "0px", "6px", "4px"),
  },
  divider: {
    display: "flex",
    ...shorthands.margin("3px", "0px"),
    ...shorthands.borderBottom(
      "1px",
      "solid",
      customTokens.colorNeutralStroke2
    ),
    flexDirection: "column",
    alignItems: "flex-start",
  },
  menuGroupHeader: {
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("8px", "12px"),
    height: "16px",
    alignSelf: "stretch",
    color: customTokens.colorNeutralForeground1,
    lineHeight: "16px",
    [`${HighContrastSelector}`]: {
      ...commonMediumContrastColorHoverFocus,
    },
  },
  splitButton: {
    display: "flex",
    alignItems: "center",
    minWidth: "unset",
    justifyContent: "flex-end",
    ...shorthands.padding("6px", "6px", "6px", "0px"),
    ...shorthands.margin("0px", "6px", "0px", "-6px"),
    ...shorthands.gap("0px"),
  },
  subMenu: {
    minWidth: "unset",
  },
  disabled: {
    ":hover:active": {
      // For some unknown reasons, the pressed state for a disabled menu item shows a bg color on it.
      // Hence, explicitly adding the correct styling to override the pressed state.
      backgroundColor: customTokens.colorNeutralBackground1,
      color: customTokens.colorNeutralForegroundDisabled,
      [`${HighContrastSelector}`]: {
        ...commonHighContrastColorDisabled,
        ...commonHighContrastHoveredDisabledStyles,
      },
    },
    ":hover": {
      [`${HighContrastSelector}`]: {
        ...commonHighContrastColorDisabled,
        ...commonHighContrastHoveredDisabledStyles,
        "& i": {
          ...commonHighContrastHoveredDisabledStyles,
        },
      },
    },
  },
  checked: {
    borderRadius: customTokens.borderRadiusMedium,
    ...shorthands.padding("0px", "5px"), // dropping the padding 1px to cater to the border
    border: `1px solid ${customTokens.colorNeutralForeground4}`,
    backgroundColor: customTokens.colorNeutralBackground1Selected,
    [`${HighContrastSelector}`]: {
      ...commonHighContrastHoverFocus,
      ...useSelectedIconHighContrastStyles,
    },
  },
  shortcutPillsTagGroup: {
    gap: "2px",
  },
  headerWithActionButton: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("8px", "12px"),
    height: "16px",
    alignSelf: "stretch",
    color: customTokens.colorNeutralForeground1,
    lineHeight: "16px",
    [`${HighContrastSelector}`]: {
      ...commonMediumContrastColorHoverFocus,
    },
  },
  headerActionButton: {
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    backgroundColor: "transparent",
    color: customTokens.colorBrandForegroundLink,
    ":hover": {
      textDecoration: "underline",
    },
  },
});

export const useAppMenuItemStyles = (state: AppMenuItemState) => {
  const styles = useAppMenuItemCustomStyles();
  const footerItemClassName = useFooterItemClassNames(
    state.footerItem,
    styles.link,
    styles.footerButton
  );

  const isV9MenuEnabled = useV9MenuEnabled();

  state.root.className = mergeClasses(
    "ms-MenuButtonNext",
    styles.root,
    state.disabled && styles.disabled,
    state.footerItem?.isFooterItem && footerItemClassName,
    state.root.className,
    isV9MenuEnabled && styles.menuItems
  );

  if (state.icon) {
    state.icon.className = mergeClasses(
      styles.icon,
      state.footerItem?.isFooterItem && styles.footerIcon,
      state.icon.className
    );
  }
  if (state.content) {
    state.content.className = mergeClasses(
      styles.content,
      state.content.className
    );
  }

  if (state.secondaryContent) {
    state.secondaryContent.className = mergeClasses(
      styles.secondaryContent,
      state.secondaryContent.className
    );
  }
  if (state.submenuIndicator) {
    state.submenuIndicator.className = mergeClasses(
      styles.secondaryContent,
      state.submenuIndicator.className
    );
  }

  useMenuItemStyles_unstable(state);
  return state;
};

export const useFooterItemClassNames = (
  footerItem?: FooterItemProps,
  linkClassName?: string,
  buttonClassName?: string
) => {
  if (!footerItem?.isFooterItem) {
    return undefined;
  }

  const itemCount = footerItem.footerItemCount ?? 0;
  const itemIndex = footerItem.footerItemIndex ?? 0;
  if (itemCount === 2 && itemIndex === 0) {
    return linkClassName;
  }

  return buttonClassName;
};
